import type { DiffSegment } from './noteInstructions.types';

// Above this many token pairs the LCS table is too large; fall back to a
// block replace of the changed middle.
const MAX_LCS_CELLS = 4_000_000;

const tokenize = (text: string): string[] => text.split(/(\s+)/).filter((token) => token.length > 0);

const pushSegment = (segments: DiffSegment[], kind: DiffSegment['kind'], text: string) => {
  const last = segments[segments.length - 1];
  if (last && last.kind === kind) {
    last.text += text;
  } else if (text) {
    segments.push({ kind, text });
  }
};

const lcsDiff = (a: string[], b: string[], segments: DiffSegment[]) => {
  const cols = b.length + 1;
  const table = new Uint32Array((a.length + 1) * cols);
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      table[i * cols + j] =
        a[i] === b[j]
          ? table[(i + 1) * cols + j + 1] + 1
          : Math.max(table[(i + 1) * cols + j], table[i * cols + j + 1]);
    }
  }
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      pushSegment(segments, 'equal', a[i]);
      i++;
      j++;
    } else if (table[(i + 1) * cols + j] >= table[i * cols + j + 1]) {
      pushSegment(segments, 'del', a[i++]);
    } else {
      pushSegment(segments, 'add', b[j++]);
    }
  }
  pushSegment(segments, 'del', a.slice(i).join(''));
  pushSegment(segments, 'add', b.slice(j).join(''));
};

// Unchanged runs this short between two edits are folded into the edit.
const MAX_BRIDGE_WORDS = 3;

const isBridge = (text: string) => !text.includes('\n') && text.split(/\s+/).filter(Boolean).length <= MAX_BRIDGE_WORDS;

const joinTexts = (segments: DiffSegment[], skip: DiffSegment['kind']) =>
  segments
    .filter((segment) => segment.kind !== skip)
    .map((segment) => segment.text)
    .join('');

/**
 * Merges edits separated only by short unchanged runs, so a rewrite reads as one phrase instead of
 * word confetti. A run is only merged when it both removes and adds text; a one-sided run (pure
 * deletions or pure insertions) keeps its unchanged words unmarked.
 */
const groupIntoPhrases = (segments: DiffSegment[]): DiffSegment[] => {
  const grouped: DiffSegment[] = [];
  let run: DiffSegment[] = [];
  const flush = () => {
    const isRewrite = run.some((s) => s.kind === 'del') && run.some((s) => s.kind === 'add');
    if (isRewrite) {
      pushSegment(grouped, 'del', joinTexts(run, 'add'));
      pushSegment(grouped, 'add', joinTexts(run, 'del'));
    } else {
      for (const segment of run) pushSegment(grouped, segment.kind, segment.text);
    }
    run = [];
  };
  segments.forEach((segment, index) => {
    const isBridgeBetweenEdits =
      segment.kind === 'equal' && run.length > 0 && !!segments[index + 1] && isBridge(segment.text);
    if (segment.kind !== 'equal' || isBridgeBetweenEdits) {
      run.push(segment);
    } else {
      flush();
      pushSegment(grouped, 'equal', segment.text);
    }
  });
  flush();
  return grouped;
};

/** Word-level diff (whitespace kept as tokens) between two note texts, grouped into changed phrases. */
export const diffWords = (before: string, after: string): DiffSegment[] => {
  const a = tokenize(before);
  const b = tokenize(after);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }

  const segments: DiffSegment[] = [];
  pushSegment(segments, 'equal', a.slice(0, start).join(''));
  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);
  if (midA.length * midB.length > MAX_LCS_CELLS) {
    pushSegment(segments, 'del', midA.join(''));
    pushSegment(segments, 'add', midB.join(''));
  } else {
    lcsDiff(midA, midB, segments);
  }
  pushSegment(segments, 'equal', a.slice(endA).join(''));
  return groupIntoPhrases(segments);
};
